import {hasHiddenUnicodeCharacters, showHiddenUnicodeCharactersRaw} from '@github-ui/hidden-unicode-banner/utils'
import {getLineHunkData, type HunkData} from '../helpers/hunk-data-helpers'
import {getDiffLineMarkersKey, isDiffLine, isEmptyDiffLine, rowIdFrom} from '../helpers/line-helpers'
import {useDiffAnchor, useDiffLineSelectionInfo, useFileLineCount} from '../stores/selectors'
import type {ClientDiffLine, DiffLine, FileDiffReference, LineKey} from '../types'
import {DiffLineSide} from './DiffLineTableRow'
import ExpandableHunkHeaderDiffLine from './ExpandableHunkHeaderDiffLine'
import {showHiddenUnicodeCharactersHTML} from '@github-ui/hidden-unicode-banner/HiddenUnicodeCharacter'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {memo, useMemo} from 'react'
import {useMarkersAtPosition} from '@github-ui/pull-requests/pull-request-store-selectors'
import type {ViewerData} from '../conversation-types'

const SplitDiffRow = memo(function SplitDiffRow({
  copilotChatReference,
  diffHasHiddenUnicodeCharacters,
  filePath,
  handleDiffSideCellSelectionBlocking,
  hunksData,
  leftDiffLine,
  nextDiffLine,
  prevDiffLine,
  rightDiffLine,
  showHiddenUnicode,
  subjectId,
  viewerData,
}: {
  copilotChatReference?: FileDiffReference
  diffHasHiddenUnicodeCharacters: boolean
  filePath: string
  handleDiffSideCellSelectionBlocking: (diffSide: 'left' | 'right') => void
  hunksData: HunkData[]
  leftDiffLine: ClientDiffLine
  nextDiffLine: ClientDiffLine | undefined
  prevDiffLine: ClientDiffLine | undefined
  rightDiffLine: ClientDiffLine
  showHiddenUnicode: boolean
  subjectId: string
  viewerData: ViewerData
}) {
  const diffAnchor = useDiffAnchor()
  const fileLineCount = useFileLineCount()

  const processLineForHiddenUnicode = (line: ClientDiffLine): [ClientDiffLine, boolean] => {
    let hasHiddenUnicode = false
    if (diffHasHiddenUnicodeCharacters && !isEmptyDiffLine(line)) {
      hasHiddenUnicode = hasHiddenUnicodeCharacters(line.text)

      if (showHiddenUnicode && hasHiddenUnicode) {
        if (line.html) {
          line = {
            ...line,
            html: showHiddenUnicodeCharactersHTML(line.html as SafeHTMLString) ?? line.html,
          }
        } else {
          line = {...line, text: showHiddenUnicodeCharactersRaw(line.text)}
        }
      }
    }
    return [line, hasHiddenUnicode]
  }

  const [leftLine, leftLineHasHiddenUnicodeCharacters] = processLineForHiddenUnicode(leftDiffLine)
  const [rightLine, rightLineHasHiddenUnicodeCharacters] = processLineForHiddenUnicode(rightDiffLine)

  const leftLineSelectionInfo = useDiffLineSelectionInfo(!isEmptyDiffLine(leftLine) ? leftLine : undefined)
  const rightLineSelectionInfo = useDiffLineSelectionInfo(!isEmptyDiffLine(rightLine) ? rightLine : undefined)

  const leftLineWithLineSelectionInfo = useMemo(() => {
    if (isEmptyDiffLine(leftLine)) return leftLine
    return {
      ...leftLine,
      ...leftLineSelectionInfo,
    }
  }, [leftLine, leftLineSelectionInfo])

  const rightLineWithLineSelectionInfo = useMemo(() => {
    if (isEmptyDiffLine(rightLine)) return rightLine
    return {
      ...rightLine,
      ...rightLineSelectionInfo,
    }
  }, [rightLine, rightLineSelectionInfo])

  const isHunkRow = isDiffLine(leftLineWithLineSelectionInfo) && leftLineWithLineSelectionInfo.type === 'HUNK'
  const rowId = rowIdFrom(diffAnchor, leftDiffLine, rightDiffLine)
  const currentDiffLine = !isEmptyDiffLine(leftLineWithLineSelectionInfo)
    ? leftLineWithLineSelectionInfo
    : !isEmptyDiffLine(rightLineWithLineSelectionInfo)
      ? rightLineWithLineSelectionInfo
      : undefined

  const leftLineMarkersKey = !isEmptyDiffLine(leftLine) ? getDiffLineMarkersKey(leftLine.left, 'LEFT') : undefined
  const {threads: leftThreadsAtPosition, annotations: leftAnnotationsAtPosition} = useMarkersAtPosition(
    filePath,
    leftLineMarkersKey ?? ('' as LineKey),
  )

  const rightLineMarkersKey = !isEmptyDiffLine(rightLine) ? getDiffLineMarkersKey(rightLine.right, 'RIGHT') : undefined
  const {threads: rightThreadsAtPosition, annotations: rightAnnotationsAtPosition} = useMarkersAtPosition(
    filePath,
    rightLineMarkersKey ?? ('' as LineKey),
  )

  const isRowSelected = useMemo(() => {
    return (!isEmptyDiffLine(leftLineWithLineSelectionInfo) && leftLineWithLineSelectionInfo.isSelected) ||
      (!isEmptyDiffLine(rightLineWithLineSelectionInfo) && rightLineWithLineSelectionInfo.isSelected)
      ? true
      : false
  }, [leftLineWithLineSelectionInfo, rightLineWithLineSelectionInfo])

  if (!currentDiffLine) return null

  const {currentHunk, nextHunk, previousHunk} = getLineHunkData(currentDiffLine, hunksData)

  if (!leftLineWithLineSelectionInfo) return null

  return (
    <tr key={rowId} className="diff-line-row">
      {isHunkRow ? (
        <ExpandableHunkHeaderDiffLine
          currentHunk={currentHunk}
          diffAnchor={diffAnchor}
          diffLine={leftDiffLine}
          fileLineCount={fileLineCount}
          isLeftSide
          isSplit
          nextHunk={nextHunk}
          nextLine={nextDiffLine as DiffLine}
          previousHunk={previousHunk}
          prevLine={prevDiffLine as DiffLine}
        />
      ) : (
        <>
          <DiffLineSide
            annotationsAtPosition={leftAnnotationsAtPosition}
            copilotChatReference={copilotChatReference}
            currentHunk={currentHunk}
            diffLine={leftLineWithLineSelectionInfo}
            diffAnchor={diffAnchor}
            filePath={filePath}
            handleDiffSideCellSelectionBlocking={handleDiffSideCellSelectionBlocking}
            hasHiddenUnicodeCharacters={leftLineHasHiddenUnicodeCharacters}
            isLeftSide
            isRowSelected={isRowSelected}
            isSplit
            nextHunk={nextHunk}
            previousHunk={previousHunk}
            rowId={rowId}
            subjectId={subjectId}
            threadsAtPosition={leftThreadsAtPosition}
            viewerData={viewerData}
          />
          <DiffLineSide
            threadsAtPosition={rightThreadsAtPosition}
            annotationsAtPosition={rightAnnotationsAtPosition}
            copilotChatReference={copilotChatReference}
            currentHunk={currentHunk}
            diffLine={rightLineWithLineSelectionInfo}
            diffAnchor={diffAnchor}
            filePath={filePath}
            handleDiffSideCellSelectionBlocking={handleDiffSideCellSelectionBlocking}
            hasHiddenUnicodeCharacters={rightLineHasHiddenUnicodeCharacters}
            isLeftSide={false}
            isRowSelected={isRowSelected}
            isSplit
            nextHunk={nextHunk}
            previousHunk={previousHunk}
            rowId={rowId}
            subjectId={subjectId}
            viewerData={viewerData}
          />
        </>
      )}
    </tr>
  )
})

export default SplitDiffRow

try{ SplitDiffRow.displayName ||= 'SplitDiffRow' } catch {}